import * as THREE from 'three';
import { Rail, createLevelRail } from '@lucky-vr/common';

/**
 * Thin THREE wrapper over the shared Rail so client visuals and server
 * hit detection sample the exact same curve.
 */
export class RailPath {
  readonly rail: Rail;

  private up = new THREE.Vector3(0, 1, 0);
  private tmpMatrix = new THREE.Matrix4();
  private tmpTarget = new THREE.Vector3();

  constructor(rail: Rail) {
    this.rail = rail;
  }

  static createLevel(level: number): RailPath {
    return new RailPath(createLevelRail(level));
  }

  get length(): number {
    return this.rail.length;
  }

  getPosition(t: number, lateral = 0, height = 0): THREE.Vector3 {
    const pose = this.rail.getPose(t, lateral, height);
    return new THREE.Vector3(pose.position.x, pose.position.y, pose.position.z);
  }

  /** Yaw at t as a quaternion (track is planar, so no pitch/roll). */
  getQuaternion(t: number): THREE.Quaternion {
    const f = this.rail.getTangent(t);
    return new THREE.Quaternion().setFromRotationMatrix(
      this.tmpMatrix.lookAt(
        new THREE.Vector3(0, 0, 0),
        this.tmpTarget.set(f.x, f.y, f.z),
        this.up
      )
    );
  }

  getTransform(t: number, lateral: number, height = 0): { position: THREE.Vector3; quaternion: THREE.Quaternion } {
    return {
      position: this.getPosition(t, lateral, height),
      quaternion: this.getQuaternion(t),
    };
  }

  /** Yaw angle in radians, for placing merged/static geometry. */
  getYaw(t: number): number {
    const f = this.rail.getTangent(t);
    return Math.atan2(f.x, f.z) + Math.PI;
  }
}
